import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Input,
  Paper,
  Snackbar,
  Typography,
} from "@mui/material";
import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "components/atoms/text";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  LinearScale,
  Title,
} from "chart.js";
import { Result, Search } from "core/types/search.types";
import { SearchTable } from "components/organisms/search-table";
import { validateUrl } from "core/helpers/validate-url";
import { researchApi } from "core/api";
import { LoadingButton } from "@mui/lab";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import { isOperatorRole } from "core/constants/roles";
import { IMPORT_LINKS_FOR_ALL } from "core/constants/feature-flags";
import { useActAs } from "contexts/ActAsContext";
import { ImportLinksDialog } from "./import-links-dialog";

ChartJS.register(LinearScale, CategoryScale, BarElement, Title);

interface OwnProps {
  selectedResearch: string;
  isResearchLoading: boolean;
  researchDetail: Search;
  isPracticeLoading: boolean;
  generatePractice: (urls: Result[]) => void;
  getSearches: () => void;
  // Ruolo di chi e' collegato (GET /account). In delega resta quello dell'operatore.
  userRole?: string;
}
export type Props = OwnProps;

const SearchDetail: FunctionComponent<Props> = ({
  selectedResearch,
  isResearchLoading,
  researchDetail,
  isPracticeLoading,
  generatePractice,
  getSearches,
  userRole,
}) => {
  const { t } = useTranslation();
  // Import da Excel/CSV: prima agli operatori (anche in delega act-as), poi a tutti con
  // IMPORT_LINKS_FOR_ALL. In delega il ruolo da /account resta quello dell'operatore;
  // la sessione aperta e' una conferma in piu' (solo un operatore puo' averne una).
  const { session: actAsSession } = useActAs();
  const canImportLinks =
    IMPORT_LINKS_FOR_ALL || isOperatorRole(userRole) || !!actAsSession;
  const [isImportOpen, setIsImportOpen] = useState(false);
  // A ricerca in corso SFM risalva il JSON dalla sua copia in memoria fino alla fine: i link
  // importati adesso verrebbero sovrascritti. Il backend risponde 409 search_running.
  const isSearchRunning = researchDetail.status === "in_progress";
  const [manualUrl, setManualUrl] = useState("");
  const [manualUrlTitle, setManualUrlTitle] = useState("");
  const [isManualAddingLoading, setIsManualAddingLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  const options: ChartOptions<"bar"> = {
    indexAxis: "y",
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  const onManualReport = async () => {
    setIsManualAddingLoading(true);
    try {
      const resp = await researchApi.addManual(
        manualUrlTitle,
        manualUrl,
        selectedResearch
      );
      if (resp) {
        setOpenSnackbar(true);
        setMessage(t("searches.success"));
        getSearches();
      }
    } catch (err) {
      setOpenSnackbar(true);
      setMessage(t("searches.error"));
      setIsError(true);
    } finally {
      setIsManualAddingLoading(false);
    }
  };

  return (
    <>
      <Paper
        sx={{
          borderRadius: "10px",
          padding: "20px",
        }}
      >
        {isResearchLoading ? (
          <div
            style={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <CircularProgress color="secondary" size={"100px"} />
          </div>
        ) : (
          <Grid container spacing={3}>
            <Grid
              item
              xs={12}
              sm={6}
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"space-between"}
            >
              <Box>
                <Text>
                  {t("searches.code")} <b>{selectedResearch}</b>
                </Text>
              </Box>

              <Box>
                <Typography variant="body1" display={"flex"}>
                  <b style={{ marginRight: "5px" }}>{t("searches.params")}:</b>{" "}
                  {(researchDetail.params ?? []).map((p, index) => (
                    <Typography key={index}>
                      {p}
                      {researchDetail.params?.length &&
                      researchDetail.params?.length - 1 !== index
                        ? ", "
                        : ""}
                    </Typography>
                  ))}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body1">
                  <b>{t("searches.total")}</b> {researchDetail.results?.length}
                </Typography>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"flex-end"}
            >
              {/* <Chart list={sourceList} showLegend /> */}
            </Grid>
          </Grid>
        )}
      </Paper>
      <Paper
        sx={{
          borderRadius: "10px",
          padding: "20px",
          mt: "20px",
        }}
      >
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          flexWrap={"wrap"}
          gap={1}
        >
          <Text fontSize={"16px"} fontWeight={"700"} paddingY={"15px"}>
            {t("report.create_custom")}
          </Text>
          {canImportLinks && (
            <Button
              variant="outlined"
              startIcon={<UploadFileRoundedIcon />}
              onClick={() => setIsImportOpen(true)}
              disabled={isSearchRunning}
            >
              {t("importLinks.open_button")}
            </Button>
          )}
        </Box>
        {canImportLinks && isSearchRunning && (
          <Text fontSize={"13px"} paddingBottom={"10px"}>
            {t("importLinks.errors.search_running")}
          </Text>
        )}

        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Input
              value={manualUrlTitle}
              onChange={(ev) => setManualUrlTitle(ev.target.value)}
              fullWidth
              placeholder={t("report.manual_create_title_placeholder")}
            />
          </Grid>
          <Grid item xs={6}>
            <Input
              value={manualUrl}
              onChange={(ev) => setManualUrl(ev.target.value)}
              fullWidth
              placeholder={t("report.manual_create_placeholder")}
            />
          </Grid>
          <Grid item xs={2}>
            <LoadingButton
              fullWidth
              variant="contained"
              onClick={onManualReport}
              disabled={!validateUrl(manualUrl) || !manualUrlTitle}
              loading={isManualAddingLoading}
            >
              {t("report.create")}
            </LoadingButton>
          </Grid>
        </Grid>
      </Paper>
      {researchDetail.results && (
        <Paper
          sx={{
            borderRadius: "10px",
            mt: "20px",
            padding: "20px",
          }}
        >
          <SearchTable
            search={researchDetail}
            detailMode
            style={{
              maxHeight: "460px",
            }}
            generatePractice={generatePractice}
            isPracticeLoading={isPracticeLoading}
          />
        </Paper>
      )}
      {canImportLinks && (
        <ImportLinksDialog
          open={isImportOpen}
          searchId={selectedResearch}
          onClose={() => setIsImportOpen(false)}
          onImported={getSearches}
        />
      )}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
        onClose={() => {
          setOpenSnackbar(false);
          setMessage("");
          setIsError(false);
        }}
      >
        <Alert
          onClose={() => {
            setOpenSnackbar(false);
            setMessage("");
            setIsError(false);
          }}
          severity={isError ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SearchDetail;
